import * as z from "zod/v4";

export const portfolioAssetTypes = [
  "equity",
  "fund",
  "option",
  "crypto",
  "prediction",
  "private",
  "cash",
  "other",
] as const;

export const portfolioSides = ["long", "short", "cash"] as const;
export const portfolioStatuses = ["active", "watching", "closed"] as const;

export type PortfolioAssetType = (typeof portfolioAssetTypes)[number];
export type PortfolioSide = (typeof portfolioSides)[number];
export type PortfolioStatus = (typeof portfolioStatuses)[number];
export type PortfolioOrigin =
  | "manual"
  | "manual-paper"
  | "webull-paper"
  | "webull-production"
  | "polymarket"
  | "polymarket-paper"
  | "kalshi-demo"
  | "kalshi-paper"
  | "kalshi-production"
  | "coinbase-paper"
  | "coinbase-production";

export const POLYMARKET_PAPER_VENUE = "Polymarket Paper";
export const MANUAL_PAPER_VENUE = "Manual Paper";
export function manualPortfolioOrigin(venue: string): PortfolioOrigin {
  return venue === POLYMARKET_PAPER_VENUE ? "polymarket-paper" : venue === MANUAL_PAPER_VENUE ? "manual-paper" : "manual";
}
export function isPaperPortfolioPosition(position: Pick<PortfolioPosition, "origin" | "venue">) {
  return position.origin === "manual-paper" || position.origin === "manual" && position.venue === MANUAL_PAPER_VENUE
    || ["webull-paper", "polymarket-paper", "kalshi-demo", "kalshi-paper", "coinbase-paper"].includes(position.origin);
}

export type PortfolioPosition = {
  id: string;
  origin: PortfolioOrigin;
  externalAccountLabel: string | null;
  syncedAt: string | null;
  symbol: string;
  name: string;
  assetType: PortfolioAssetType;
  side: PortfolioSide;
  status: PortfolioStatus;
  quantity: number;
  unitCost: number | null;
  markPrice: number | null;
  currency: string;
  venue: string;
  thesis: string;
  notes: string;
  riskLimitPct: number | null;
  sourceIds: string[];
  systemIds: string[];
  simulationIds: string[];
  projectionIds: string[];
  openedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const optionalPrice = z.number().finite().nonnegative().nullable().default(null);
const linkedIds = z.array(z.string().trim().min(1).max(160)).max(32).default([]);
const optionalDate = z.union([
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  z.null(),
]).default(null);

export const portfolioPositionInputSchema = z.object({
  symbol: z.string().trim().min(1).max(32).transform((value) => value.toUpperCase()),
  name: z.string().trim().min(1).max(120),
  assetType: z.enum(portfolioAssetTypes),
  side: z.enum(portfolioSides),
  status: z.enum(portfolioStatuses),
  quantity: z.number().finite().nonnegative(),
  unitCost: optionalPrice,
  markPrice: optionalPrice,
  currency: z.literal("USD"),
  venue: z.string().trim().max(80).default(""),
  thesis: z.string().trim().max(4_000).default(""),
  notes: z.string().trim().max(4_000).default(""),
  riskLimitPct: z.number().finite().min(0).max(100).nullable().default(null),
  sourceIds: linkedIds,
  systemIds: linkedIds,
  simulationIds: linkedIds,
  projectionIds: linkedIds,
  openedAt: optionalDate,
});

export type PortfolioPositionInput = z.infer<typeof portfolioPositionInputSchema>;

export type PortfolioPositionMetrics = {
  marketValue: number | null;
  unrealizedPnl: number | null;
  grossWeightPct: number | null;
  overRiskLimit: boolean;
};

export type PortfolioSummary = {
  netMarkedValue: number;
  grossExposure: number;
  longExposure: number;
  shortExposure: number;
  cash: number;
  unrealizedPnl: number;
  activeCount: number;
  missingMarkCount: number;
  riskBreachCount: number;
};

export type PortfolioConnectionState = {
  status: "live" | "not_configured" | "error";
  environment: "paper" | "live";
  accountCount: number;
  positionCount: number;
  observedAt: string | null;
  message: string;
};

export type WebullCapability = { status: 'available' | 'subscription_required' | 'unavailable'; code?: string; checkedAt: string };
export type WebullConnectionState = PortfolioConnectionState & {
  accounts: { label: string; currency: string | null; cashBalance: number | null; netLiquidationValue: number | null; status: string }[];
  capabilities: Partial<Record<'stocks' | 'options' | 'optionContracts', WebullCapability>>;
  ordersEnabled: false;
};
export const emptyWebullProductionConnection: WebullConnectionState = {
  status: 'not_configured', environment: 'live', accountCount: 0, positionCount: 0, observedAt: null,
  message: 'Webull account access is not configured in this environment.', accounts: [], capabilities: {}, ordersEnabled: false,
};

export function positionMarketValue(position: PortfolioPosition) {
  if (position.markPrice === null || position.currency !== "USD") return null;
  return position.quantity * position.markPrice;
}

export function positionUnrealizedPnl(position: PortfolioPosition) {
  if (position.markPrice === null || position.unitCost === null || position.currency !== "USD") return null;
  const raw = position.quantity * (position.markPrice - position.unitCost);
  return position.side === "short" ? -raw : raw;
}

export function calculatePortfolio(positions: PortfolioPosition[]) {
  const active = positions.filter((position) => position.status === "active");
  let cash = 0;
  let longExposure = 0;
  let shortExposure = 0;
  let unrealizedPnl = 0;
  let missingMarkCount = 0;

  active.forEach((position) => {
    const marketValue = positionMarketValue(position);
    if (marketValue === null) {
      missingMarkCount += 1;
      return;
    }
    if (position.side === "cash" || position.assetType === "cash") cash += marketValue;
    else if (position.side === "short") shortExposure += marketValue;
    else longExposure += marketValue;

    unrealizedPnl += positionUnrealizedPnl(position) ?? 0;
  });

  const grossExposure = longExposure + shortExposure;
  const grossTrackedValue = grossExposure + Math.abs(cash);
  const metrics = new Map<string, PortfolioPositionMetrics>();
  let riskBreachCount = 0;

  positions.forEach((position) => {
    const marketValue = positionMarketValue(position);
    const grossWeightPct = position.status === "active" && marketValue !== null && grossTrackedValue > 0
      ? (Math.abs(marketValue) / grossTrackedValue) * 100
      : null;
    const overRiskLimit = grossWeightPct !== null
      && position.riskLimitPct !== null
      && grossWeightPct > position.riskLimitPct;
    if (overRiskLimit) riskBreachCount += 1;
    metrics.set(position.id, {
      marketValue,
      unrealizedPnl: positionUnrealizedPnl(position),
      grossWeightPct,
      overRiskLimit,
    });
  });

  const summary: PortfolioSummary = {
    netMarkedValue: cash + longExposure - shortExposure,
    grossExposure,
    longExposure,
    shortExposure,
    cash,
    unrealizedPnl,
    activeCount: active.length,
    missingMarkCount,
    riskBreachCount,
  };

  return { summary, metrics };
}
